// ===================================================================
// Impact — Reports
// Generate FEMA forms (ICS-209, PDA, PA), CSV export, date filtering
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Group, Badge, Button,
  LoadingOverlay, Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconFileText, IconDownload, IconFileSpreadsheet,
  IconPhoto, IconFileAnalytics,
} from '@tabler/icons-react';
import { format } from 'date-fns';
import { useImpactStore } from '../../store/impactStore';
import { useAuthStore } from '../../store/authStore';
import {
  generateICS209, generatePDASummary, generatePASummary, assessmentsToCSV,
  generatePhotoDocReport, type PhotoDocEntry,
} from './lib/femaForms';
import { toast } from './lib/toast';
import { primary } from '../../lib/colors';

// ── Report Type Definitions ────────────────────────────────

interface ReportType {
  id: string;
  title: string;
  description: string;
  icon: typeof IconFileText;
  color: string;
  action: () => void;
}

// ── Component ──────────────────────────────────────────────

export default function Reports() {
  const { assessments, isLoading, loadAssessments } = useImpactStore();
  const { activeIncident, user } = useAuthStore();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [preview, setPreview] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  // Adapter: create a disaster-like object for report generators
  const incidentForReport = useMemo(() => {
    if (!activeIncident) return null;
    return {
      name: activeIncident.name || 'Unknown',
      id: String(activeIncident.id),
      incidentType: activeIncident.incidentType || '',
      location: activeIncident.location || '',
      startDate: activeIncident.startDate || '',
    };
  }, [activeIncident]);

  const orgName = 'OpenGov EMC';

  // Filter assessments by date range
  const filtered = useMemo(() => {
    if (!dateRange[0] && !dateRange[1]) return assessments;

    return assessments.filter(a => {
      if (!a.datecreated) return false;
      const d = new Date(a.datecreated);
      if (isNaN(d.getTime())) return false;
      if (dateRange[0] && d < dateRange[0]) return false;
      if (dateRange[1]) {
        const endOfDay = new Date(dateRange[1]);
        endOfDay.setHours(23, 59, 59, 999);
        if (d > endOfDay) return false;
      }
      return true;
    });
  }, [assessments, dateRange]);

  // ── Report Actions ─────────────────────────────────────

  const handleICS209 = () => {
    if (!incidentForReport) {
      toast.error('No active incident selected');
      return;
    }
    setGenerating(true);
    try {
      const doc = generateICS209(incidentForReport, filtered, orgName);
      const name = `ICS209_${incidentForReport.name.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      doc.save(name);
      toast.success(`ICS-209 saved as ${name}`);
    } catch (err) {
      toast.error('Failed to generate ICS-209: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  const handlePDASummary = () => {
    if (!incidentForReport) {
      toast.error('No active incident selected');
      return;
    }
    setGenerating(true);
    try {
      const doc = generatePDASummary(incidentForReport, filtered, orgName);
      const name = `PDA_Summary_${incidentForReport.name.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      doc.save(name);
      toast.success(`PDA Summary saved as ${name}`);
    } catch (err) {
      toast.error('Failed to generate PDA Summary: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  const handlePASummary = () => {
    if (!incidentForReport) {
      toast.error('No active incident selected');
      return;
    }
    setGenerating(true);
    try {
      const doc = generatePASummary(incidentForReport, filtered, orgName);
      const name = `PA_Summary_${incidentForReport.name.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      doc.save(name);
      toast.success(`PA Summary saved as ${name}`);
    } catch (err) {
      toast.error('Failed to generate PA Summary: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  const handleCSV = () => {
    setGenerating(true);
    try {
      const csv = assessmentsToCSV(filtered);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Impact_Assessments_${format(new Date(), 'yyyyMMdd')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filtered.length} assessments to CSV`);
    } catch (err) {
      toast.error('Failed to export CSV: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  const handlePhotoExport = async () => {
    if (!incidentForReport) {
      toast.error('No active incident selected');
      return;
    }
    if (filtered.length === 0) {
      toast.warning('No assessments match the current filters');
      return;
    }
    setGenerating(true);
    try {
      const { loadPhotos } = useImpactStore.getState();
      const entries: PhotoDocEntry[] = [];
      for (const a of filtered) {
        const photos = await loadPhotos(a.id as unknown as number);
        for (const p of photos) {
          if (!p.photoUrl) continue;
          entries.push({
            base64: p.photoUrl,
            caption: p.caption || a.description || '',
            address: a.address || '',
            county: a.county || '',
            damageCategory: a.damageCategory || '',
            severity: String(a.severity || ''),
            latitude: p.exifLat || String(a.latitude || ''),
            longitude: p.exifLng || String(a.longitude || ''),
            exifDate: p.exifDate,
            assessmentDate: a.datecreated,
            assessorName: a.assessorName,
          });
        }
      }
      if (entries.length === 0) {
        toast.warning('No photos found on the filtered assessments');
        return;
      }
      const doc = await generatePhotoDocReport(incidentForReport, entries, orgName);
      const name = `FEMA_009-0-91D_PhotoDoc_${incidentForReport.name.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      doc.save(name);
      toast.success(`Photo Documentation saved as ${name} (${entries.length} photo${entries.length !== 1 ? 's' : ''})`);
    } catch (err) {
      toast.error('Failed to generate Photo Documentation: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = () => {
    if (filtered.length === 0) {
      toast.warning('No assessments match the current filters');
      return;
    }

    const iaCount = filtered.filter(a => a.assessmentType === 'individual').length;
    const paCount = filtered.filter(a => a.assessmentType === 'public').length;
    const categories = Object.entries(
      filtered.reduce<Record<string, number>>((acc, a) => {
        const cat = a.damageCategory || 'uncategorized';
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {})
    ).map(([k, v]) => `  ${k}: ${v}`).join('\n');

    const counties = Object.entries(
      filtered.reduce<Record<string, number>>((acc, a) => {
        const c = a.county || 'Unknown';
        acc[c] = (acc[c] || 0) + 1;
        return acc;
      }, {})
    ).map(([k, v]) => `  ${k}: ${v}`).join('\n');

    const text = [
      `IMPACT REPORT PREVIEW`,
      `Incident: ${activeIncident?.name || 'All'}`,
      `Date: ${format(new Date(), 'MM/dd/yyyy HH:mm')}`,
      `Assessor: ${user?.fullName || 'Unknown'}`,
      ``,
      `--- SUMMARY ---`,
      `Total Assessments: ${filtered.length}`,
      `Individual (IA): ${iaCount}`,
      `Public (PA): ${paCount}`,
      ``,
      `--- BY CATEGORY ---`,
      categories,
      ``,
      `--- BY COUNTY ---`,
      counties,
    ].join('\n');

    setPreview(text);
  };

  // ── Report Type Cards ──────────────────────────────────

  const reportTypes: ReportType[] = [
    {
      id: 'ics209',
      title: 'ICS-209',
      description: 'Incident Status Summary with damage breakdown, severity distribution, and incident details.',
      icon: IconFileText,
      color: '#112D4E',
      action: handleICS209,
    },
    {
      id: 'pda',
      title: 'PDA Summary',
      description: 'Preliminary Damage Assessment detail report for Individual Assistance (IA) assessments.',
      icon: IconFileAnalytics,
      color: primary[700],
      action: handlePDASummary,
    },
    {
      id: 'pa',
      title: 'PA Summary',
      description: 'Public Assistance damage summary for infrastructure and public facility assessments.',
      icon: IconFileAnalytics,
      color: '#7c3aed',
      action: handlePASummary,
    },
    {
      id: 'csv',
      title: 'CSV Export',
      description: 'Export all assessment data as a comma-separated values file for use in spreadsheets.',
      icon: IconFileSpreadsheet,
      color: '#16a34a',
      action: handleCSV,
    },
    {
      id: 'photos',
      title: 'FEMA 009-0-91D Photo Doc',
      description: 'Photo documentation sheet — 2 photos per page with caption, location, coordinates, severity, and photographer.',
      icon: IconPhoto,
      color: '#d97706',
      action: handlePhotoExport,
    },
  ];

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading || generating} />

      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Reports</Title>
          {activeIncident && (
            <Badge size="sm" variant="light" color="blue">{activeIncident.name}</Badge>
          )}
        </Group>
        <Badge size="sm" variant="outline">{filtered.length} assessments</Badge>
      </Group>

      {/* No incident warning */}
      {!activeIncident && (
        <Paper p="md" radius="sm" withBorder mb="md" bg="var(--warning-light)">
          <Text size="sm" c="var(--warning)">
            No active incident selected. Reports will include all assessments. Select an incident from the top bar to filter.
          </Text>
        </Paper>
      )}

      {/* Date Range Filter */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <Group gap="md" align="end">
          <DatePickerInput
            type="range"
            label="Filter by date range"
            placeholder="All dates"
            value={dateRange}
            onChange={(v) => setDateRange(v as [Date | null, Date | null])}
            clearable
            size="sm"
            style={{ flex: 1, maxWidth: 340 }}
          />
          <Button
            variant="light"
            size="sm"
            onClick={() => setDateRange([null, null])}
            disabled={!dateRange[0] && !dateRange[1]}
          >
            Clear
          </Button>
          <Button
            variant="light"
            color="blue"
            size="sm"
            onClick={handlePreview}
          >
            Preview
          </Button>
        </Group>
        {dateRange[0] && (
          <Text size="xs" c="dimmed" mt="xs">
            Showing assessments from {format(dateRange[0], 'MMM d, yyyy')}
            {dateRange[1] ? ` to ${format(dateRange[1], 'MMM d, yyyy')}` : ' onwards'}
            {' '} ({filtered.length} of {assessments.length})
          </Text>
        )}
      </Paper>

      {/* Report Type Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} mb="md">
        {reportTypes.map(report => {
          const Icon = report.icon;
          return (
            <Paper
              key={report.id}
              p="md"
              radius="sm"
              withBorder
              style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
              onClick={report.action}
            >
              <Group gap="sm" mb="xs">
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: report.color + '15',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={18} color={report.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text fw={600} size="sm">{report.title}</Text>
                </div>
                <IconDownload size={16} color="var(--text-secondary)" />
              </Group>
              <Text size="xs" c="dimmed" lineClamp={2}>{report.description}</Text>
            </Paper>
          );
        })}
      </SimpleGrid>

      {/* Preview Area */}
      {preview && (
        <Paper p="md" radius="sm" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">Report Preview</Text>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => setPreview(null)}
            >
              Close
            </Button>
          </Group>
          <Textarea
            value={preview}
            readOnly
            autosize
            minRows={10}
            maxRows={30}
            styles={{
              input: {
                fontFamily: 'DM Mono, monospace',
                fontSize: 12,
                background: 'var(--bg-secondary)',
              },
            }}
          />
        </Paper>
      )}
    </div>
  );
}
